import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import PageHeader from '../../components/common/PageHeader';
import { Link } from 'react-router-dom';
import { FaCoins, FaCalculator, FaRegLifeRing, FaUniversity } from 'react-icons/fa';

const FinancialServicePage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const breadcrumbs = [
    { label: 'Services', path: '/services' },
    { label: 'Financial Service' }
  ];

  const financialServices = [
    {
      icon: <FaCoins className="w-8 h-8 text-gray-700" />,
      title: "Wealth Management",
      description: "Comprehensive wealth management solutions that integrate investment strategies, tax planning, and estate planning to optimize your financial resources."
    },
    {
      icon: <FaCalculator className="w-8 h-8 text-gray-700" />,
      title: "Tax Planning",
      description: "Strategic tax planning services to minimize tax liabilities and maximize after-tax returns on your investments and business activities."
    },
    {
      icon: <FaRegLifeRing className="w-8 h-8 text-gray-700" />,
      title: "Retirement Planning",
      description: "Customized retirement planning strategies to ensure financial security and maintain your desired lifestyle throughout retirement."
    },
    {
      icon: <FaUniversity className="w-8 h-8 text-gray-700" />,
      title: "Estate Planning",
      description: "Sophisticated estate planning solutions to protect your assets, minimize estate taxes, and create a lasting legacy for future generations."
    }
  ];

  const clientTestimonials = [
    {
      quote: "Blockfirst Capital's financial services have completely transformed our approach to wealth management. Their expertise has been invaluable in helping us achieve our long-term financial goals.",
      author: "James Roberts",
      position: "CEO, Horizon Technologies"
    },
    {
      quote: "The tax planning strategies implemented by Blockfirst Capital have significantly reduced our tax burden while ensuring full compliance. Their attention to detail is exceptional.",
      author: "Sarah Johnson",
      position: "CFO, Meridian Enterprises"
    },
    {
      quote: "Thanks to Blockfirst Capital's retirement planning services, I now have a clear roadmap to financial security in retirement. Their personalized approach makes all the difference.",
      author: "Michael Chen",
      position: "Business Owner"
    }
  ];

  return (
    <>
      <PageHeader
        title="Financial Services"
        breadcrumbs={breadcrumbs}
      />

      <section className="py-8 md:py-24 bg-background">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-primary">
                Comprehensive Financial Services
              </h2>
              <p className="text-lg text-gray-700">
                Blockfirst Capital offers a complete suite of financial services designed to address every aspect of your financial life, from daily cash flow management to long-term wealth preservation.
              </p>
              <p className="text-lg text-gray-700">
                Our team of experienced financial professionals works closely with you to understand your unique needs and develop tailored solutions that align with your goals and values.
              </p>
              <div className="pt-4">
                <Link 
                  to="/contact-us" 
                  className="px-8 py-3 bg-gray-900 hover:bg-gray-800 text-white font-semibold rounded-lg transition-colors inline-block"
                >
                  Explore Our Services
                </Link>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
            >
              <img 
                src="/assets/co-workers-discussing-business-project.jpg" 
                alt="Financial Services Team" 
                className="rounded-xl shadow-lg w-full h-auto object-cover"
              />
            </motion.div>
          </div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h3 className="text-2xl md:text-3xl font-heading font-bold text-primary mb-4">
              Our Financial Services
            </h3>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              We offer a wide range of financial services to help you build, manage, and preserve your wealth.
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-20">
            {financialServices.map((service, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-xl p-8 shadow-md hover:shadow-lg transition-all duration-300"
              >
                <div className="mb-4">{service.icon}</div>
                <h4 className="text-xl font-heading font-semibold mb-3">{service.title}</h4>
                <p className="text-gray-600">{service.description}</p>
              </motion.div>
            ))}
          </div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h3 className="text-2xl md:text-3xl font-heading font-bold text-primary mb-4">
              Client Testimonials
            </h3>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Hear what our clients have to say about our financial services.
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            {clientTestimonials.map((testimonial, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-xl p-8 shadow-md"
              >
                <div className="text-4xl text-primary opacity-30 mb-4">"</div>
                <p className="text-gray-700 italic mb-6">{testimonial.quote}</p>
                <div className="border-t pt-4">
                  <p className="font-semibold text-primary">{testimonial.author}</p>
                  <p className="text-sm text-gray-500">{testimonial.position}</p>
                </div>
              </motion.div>
            ))}
          </div>
          
          <div className="bg-primary text-white rounded-xl p-10 shadow-lg">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="text-center mb-8"
            >
              <h3 className="text-2xl md:text-3xl font-heading font-bold mb-4">Ready to Secure Your Financial Future?</h3>
              <p className="text-lg text-gray-300 max-w-3xl mx-auto">
                Our financial experts are ready to help you navigate your financial journey with confidence.
              </p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <Link 
                to="/contact-us" 
                className="px-8 py-3 bg-white text-primary hover:bg-gray-100 font-semibold rounded-lg transition-colors inline-block"
              >
                Schedule a Free Consultation
              </Link>
            </motion.div>
          </div>
        </div>
      </section>
    </>
  );
};

export default FinancialServicePage;
